import React from 'react';
import { toast } from 'react-toastify';
import { mockUser } from './mock';
import { apiGet } from '../common/amplifyApi';
import {
    signIn as cognitoSignIn,
    signOut as cognitoSignOut,
    getCurrentUser as getCognitoUser,
    fetchUserAttributes,
    fetchAuthSession,
    resetPassword,
    confirmResetPassword,
    confirmSignIn,
} from 'aws-amplify/auth';

import config from '../../src/config';

var UserStateContext = React.createContext();
var UserDispatchContext = React.createContext();

const apiName = config.apiName;

const isOrthoUser = (data) => {
    const userGroups = data?.idTokenPayload?.['cognito:groups'] || [];
    return RegExp('ortho', 'gi').test(userGroups);
};

// Use localStorage for isAuthenticated to avoid re-routing after a page refresh
const initialData = {
    isLoading: false,
    isAuthenticated: localStorage.getItem('isAuthenticated'),
    isOrtho: false,
    currentUser: null,
    territory: null,
    errorMessage: '',
};

function userReducer(state, { type, payload }) {
    switch (type) {
        case 'AUTH_STARTED':
            return { ...state, isLoading: true, errorMessage: '' };
        case 'LOGIN_SUCCESS':
            return { ...state, currentUser: payload, isLoading: false };
        case 'AUTH_SUCCESS':
            return {
                ...state,
                isAuthenticated: true,
                currentUser: payload,
                isOrtho: isOrthoUser(payload),
                territory: payload.practice?.territory,
                isLoading: false,
            };
        case 'AUTH_ERROR':
            return { ...state, isAuthenticated: false, currentUser: null, isLoading: false, errorMessage: payload };
        case 'RESET_PASSWORD_STARTED':
        case 'FORGOT_PASSWORD_STARTED':
        case 'CHANGE_PASSWORD_STARTED':
            return { ...state, isLoading: true, errorMessage: '' };
        case 'RESET_PASSWORD_SUCCESS':
        case 'FORGOT_PASSWORD_SUCCESS':
        case 'CHANGE_PASSWORD_SUCCESS':
            return { ...state, isLoading: false, errorMessage: '' };
        case 'RESET_PASSWORD_ERROR':
        case 'FORGOT_PASSWORD_ERROR':
        case 'CHANGE_PASSWORD_ERROR':
            return { ...state, isLoading: false, errorMessage: payload };
        case 'SIGN_OUT_SUCCESS':
            return { ...initialData, isAuthenticated: localStorage.getItem('isAuthenticated') };
        default: {
            return state;
        }
    }
}

function UserProvider({ children }) {
    var [state, dispatch] = React.useReducer(userReducer, initialData);

    return (
        <UserStateContext.Provider value={state}>
            <UserDispatchContext.Provider value={dispatch}>{children}</UserDispatchContext.Provider>
        </UserStateContext.Provider>
    );
}

function useUserState() {
    var context = React.useContext(UserStateContext);
    if (context === undefined) {
        throw new Error('useUserState must be used within a UserProvider');
    }
    return context;
}

function useUserDispatch() {
    var context = React.useContext(UserDispatchContext);
    if (context === undefined) {
        throw new Error('useUserDispatch must be used within a UserProvider');
    }
    return context;
}

// ###########################################################

const actions = {
    signIn: (email, password, history) => async (dispatch) => {
        dispatch({ type: 'AUTH_STARTED' });
        if (!config.isBackend) {
            setTimeout(() => {
                localStorage.setItem('isAuthenticated', true);
                actions.getCurrentUser(history)(dispatch);
            }, 1000);
        } else {
            try {
                const { isSignedIn, nextStep } = await cognitoSignIn({ username: email, password });
                if (!isSignedIn && nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                    dispatch({ type: 'LOGIN_SUCCESS', payload: nextStep });
                    history.push('/password-reset');
                } else {
                    localStorage.setItem('isAuthenticated', true);
                    actions.getCurrentUser(history)(dispatch);
                }
            } catch (error) {
                console.error('Unable to login', error);
                dispatch({ type: 'AUTH_ERROR', payload: 'Something is wrong with your login or password' });
            }
        }
    },

    signOut: (history) => async (dispatch) => {
        if (config.isBackend) cognitoSignOut();
        localStorage.removeItem('isAuthenticated');
        dispatch({ type: 'SIGN_OUT_SUCCESS' });
        history.push('/login');
    },

    getCurrentUser: (history) => async (dispatch) => {
        if (!config.isBackend) return dispatch({ type: 'AUTH_SUCCESS', payload: mockUser });
        try {
            const [cognitoUser, attributes, session, practice] = await Promise.all([
                getCognitoUser(),
                fetchUserAttributes(),
                fetchAuthSession(),
                apiGet(apiName, '/v2/practice'),
            ]);
            const payload = {
                ...cognitoUser,
                attributes,
                idTokenPayload: session.tokens?.idToken?.payload,
                practice,
            };
            dispatch({ type: 'AUTH_SUCCESS', payload });
        } catch (error) {
            console.error('Unable to get current user, signing out');
            actions.signOut(history)(dispatch);
        }
    },

    forgotPassword: (email, history) => async (dispatch) => {
        if (!config.isBackend) return history.push('/password-change');
        try {
            dispatch({ type: 'FORGOT_PASSWORD_STARTED' });
            await resetPassword({ username: email });
            dispatch({ type: 'FORGOT_PASSWORD_SUCCESS' });
            toast.success('A reset password code has been sent to your email');
            history.push('/password-change');
        } catch (error) {
            // do not show error message if the user does not exist
            if (error.name === 'UserNotFoundException') {
                dispatch({ type: 'FORGOT_PASSWORD_SUCCESS' });
                toast.success('A reset password code has been sent to your email');
                return history.push('/password-change');
            }
            // show error message if the user exists
            toast.error('Unable to send reset password email');
            console.error('Unable to send reset password email', error);
            dispatch({ type: 'FORGOT_PASSWORD_ERROR', payload: error.message });
        }
    },

    changePassword: (email, verificationCode, newPassword, history) => async (dispatch) => {
        if (!config.isBackend) return history.push('/login');
        try {
            dispatch({ type: 'CHANGE_PASSWORD_STARTED' });
            await confirmResetPassword({ username: email, confirmationCode: verificationCode, newPassword });
            dispatch({ type: 'CHANGE_PASSWORD_SUCCESS' });
            // Clean up the stored email
            sessionStorage.removeItem('passwordResetEmail');
            toast.success('Your password has been updated');
            history.push('/login');
        } catch (error) {
            console.error('Unable to change password', error);
            dispatch({ type: 'CHANGE_PASSWORD_ERROR', payload: error.message });
        }
    },

    /**
     * Completes the new-password challenge for the in-progress sign-in
     * @param user truthy marker that a challenge is pending (v6 tracks the challenge state internally)
     * @param password the new password
     */
    completeNewPassword: (user, password, history) => async (dispatch) => {
        if (!config.isBackend || !user) return history.push('/login');
        try {
            dispatch({ type: 'RESET_PASSWORD_STARTED' });
            // confirmSignIn completes authentication directly, no separate re-sign-in needed
            await confirmSignIn({ challengeResponse: password });

            dispatch({ type: 'RESET_PASSWORD_SUCCESS' });
            toast.success('Your password has been updated');

            localStorage.setItem('isAuthenticated', true);
            actions.getCurrentUser(history)(dispatch);
        } catch (error) {
            console.error('Unable to change password', error);
            dispatch({ type: 'RESET_PASSWORD_ERROR', payload: error.message });
        }
    },
};

export { UserProvider, useUserState, useUserDispatch, actions };
